'use client';

import {useEffect, useState} from 'react';
import Link from 'next/link';
import {useSession, signOut} from 'next-auth/react';
import {ArrowRight, Menu, Moon, Sun, LogOut} from 'lucide-react';
import {Avatar, AvatarImage, AvatarFallback} from '@manaboo/ui/avatar';
import {Button} from '@manaboo/ui/button';
import {CoinsBoos, Logo} from '@manaboo/ui/icons';
import {Container} from '@manaboo/ui/container';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@manaboo/ui/dropdown-menu';
import {SidebarTrigger} from '@manaboo/ui/sidebar';
import {Tooltip, TooltipContent, TooltipTrigger} from '@manaboo/ui/tooltip';
import {useDarkMode} from '@manaboo/ui/hooks/use-dark-mode';
import {plnToPoints} from '@manaboo/utils/points';
import {cn} from '@manaboo/ui/cn';
import {HeaderBanner} from '@/components/header-banner.tsx';

interface HeaderProps {
	walletAmount: number;
}

export function Header({walletAmount}: HeaderProps) {
	const {data: session, status} = useSession();
	const {isDarkMode, toggleDarkMode} = useDarkMode();

	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
	}, []);

	return (
		<div className="sticky top-0 z-50">
			<HeaderBanner />

			<header
				className="h-16 shrink-0 bg-white backdrop-blur-sm dark:bg-transparent dark:bg-gradient-to-b dark:from-black/80 dark:to-black/0"
				data-testid="header"
			>
				<Container
					className={cn(
						'grid py-4',
						session?.user ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-3',
					)}
				>
					<Link
						aria-label="manaboo.gg"
						className="flex items-center gap-1.5"
						href="/"
					>
						<Logo data-testid="header-logo" />
					</Link>

					{session?.user ? (
						<div className="hidden justify-center md:flex">
							<Tooltip>
								<TooltipTrigger asChild>
									<Link
										className="dark:bg-background flex items-center rounded-md border p-1 pl-2"
										href="/portfel"
									>
										<div className="mr-3 flex items-center gap-1">
											<CoinsBoos className="h-4 w-4 dark:text-white" />
											{plnToPoints(walletAmount)}
											zł
										</div>

										<Button
											size="iconSm"
											variant="secondary"
										>
											<ArrowRight className="h-4 w-4" />
										</Button>
									</Link>
								</TooltipTrigger>
								<TooltipContent>Stan portfela</TooltipContent>
							</Tooltip>
						</div>
					) : (
						<div />
					)}

					<div className="flex items-center justify-end gap-2">
						<Button
							data-testid="header-toggle-dark-mode"
							onClick={toggleDarkMode}
							size="icon"
							variant="ghost"
						>
							{/* Hydration error workaround */}
							{isClient ? (
								<>
									{isDarkMode ? (
										<Sun className="h-4 w-4" />
									) : (
										<Moon className="h-4 w-4" />
									)}
								</>
							) : null}
						</Button>

						{session?.user ? (
							<>
								<DropdownMenu>
									<DropdownMenuTrigger>
										<Avatar
											className="bg-muted"
											data-testid="header-avatar"
										>
											<AvatarImage
												alt={session.user.name}
												src={session.user.image}
											/>
											<AvatarFallback>
												{session.user.name.slice(0, 2)}
											</AvatarFallback>
										</Avatar>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										<DropdownMenuLabel>
											{session.user.name}
										</DropdownMenuLabel>

										<DropdownMenuSeparator />

										<DropdownMenuItem
											className="flex items-center gap-1"
											onClick={() => void signOut()}
										>
											<LogOut className="h-4 w-4" />
											<span>Wyloguj</span>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>

								<SidebarTrigger>
									<Menu />
								</SidebarTrigger>
							</>
						) : null}
					</div>
				</Container>
			</header>
		</div>
	);
}
